import {useMemo} from "react";
import {dibAdminApi} from "../../dibAdminApi/dibAdminApi";
import {usePortalData} from "./usePortalData";
import {groupingSlug} from "./PortalsPage";
import {useNewsFilters} from "./useNewsFilters";

export type NewsGroupingOption = {
    slug: string;
    name: string;
    dbIds: number[];
};

/**
 * Portal groupings as news filters: the same relevance-filtered groupings the
 * portals tab shows, narrowed to those whose products have at least one news
 * article (per v2/databaseIds). Selecting groupings (slugs in the URL, see
 * useNewsFilters) filters news by the union of their product database ids.
 */
export function useNewsGroupings() {
    const {relevantGroupings, isLoading: portalLoading} = usePortalData();
    const {data: newsDbIds, isLoading: idsLoading} =
        dibAdminApi.endpoints.getNewsDatabaseIds.useQuery({});
    const {groupings: selectedSlugs, toggleValue, clear} = useNewsFilters();

    const options = useMemo<NewsGroupingOption[]>(() => {
        const withNews = new Set(newsDbIds ?? []);
        return relevantGroupings.flatMap((g) => {
            if (!g.name || !g.products_json) return [];
            const dbIds = (JSON.parse(g.products_json) as string[]).map(
                Number
            );
            if (!dbIds.some((id) => withNews.has(id))) return [];
            return [{slug: groupingSlug(g.name, g.id), name: g.name, dbIds}];
        });
    }, [relevantGroupings, newsDbIds]);

    // Union of the selected groupings' product ids, ascending — sorted so the
    // news query's cache key is stable. Stale slugs (e.g. old links) are
    // silently ignored.
    const databaseIds = useMemo(() => {
        const ids = new Set<number>();
        for (const option of options) {
            if (!selectedSlugs.includes(option.slug)) continue;
            for (const id of option.dbIds) ids.add(id);
        }
        return [...ids].sort((a, b) => a - b);
    }, [options, selectedSlugs]);

    return {
        options,
        selectedSlugs,
        toggle: (slug: string) => toggleValue("groupings", slug),
        clear: () => clear("groupings"),
        databaseIds,
        isLoading: portalLoading || idsLoading,
    };
}
